import { Directive, ElementRef, HostListener, Input, Output, EventEmitter, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export const MASK_INPUT_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MaskInputDirective),
    multi: true
};

@Directive({
    selector: '[appMaskInput]',
    providers: [MASK_INPUT_VALUE_ACCESSOR],
    standalone: false
})
export class MaskInputDirective implements ControlValueAccessor {
    private rawDigits: string = '';
    private isInternalUpdate = false;
    @Input() maxDigits?: number;
    @Input() maskType?: string;
    @Output() rawValueChange = new EventEmitter<string>();
    private onChange: (value: any) => void = () => { };
    private onTouched: () => void = () => { };
    el = inject(ElementRef);

    constructor() { }

    writeValue(value: any): void {
        if (value) {
            if (this.maskType === "'exp'") {
                this.rawDigits = value.replace(/\D/g, '');
            } else if (this.maskType === "'card'") {
                this.rawDigits = value.replace(/-/g, '');
            } else {
                this.rawDigits = value;
            }
            this.updateDisplay();
            this.rawValueChange.emit(this.rawDigits);
        } else {
            this.rawDigits = '';
            this.updateDisplay();
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.el.nativeElement.disabled = isDisabled;
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent) {
        const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
        if (allowedKeys.includes(event.key)) {
            if (event.key === 'Backspace' && this.rawDigits.length > 0) {
                this.rawDigits = this.rawDigits.slice(0, -1);
                event.preventDefault();
                this.updateValueAndDisplay();
            }
            return;
        }

        if (/\d/.test(event.key)) {
            if (this.maxDigits && this.rawDigits.length >= this.maxDigits) {
                event.preventDefault();
                return;
            }
            event.preventDefault();
            this.rawDigits += event.key;
            this.updateValueAndDisplay();
        }
    }
// Catches autofill/browser-set values that bypass keydown
    @HostListener('input', ['$event'])
    onInput(event: Event): void {
        if (this.isInternalUpdate) {
            return;
        }
        const target = event.target as HTMLInputElement;
        const digits = target.value.replace(/\D/g, '');
        this.rawDigits = this.maxDigits ? digits.substring(0, this.maxDigits) : digits;
        this.updateValueAndDisplay();
    }
    @HostListener('paste', ['$event'])
    onPaste(event: ClipboardEvent) {
        event.preventDefault();
        const pastedData = event.clipboardData?.getData('text') ?? '';
        const digits = pastedData.replace(/\D/g, '');
        let allowedDigits = digits;
        if (this.maxDigits) {
            allowedDigits = digits.substring(0, this.maxDigits - this.rawDigits.length);
        }
        this.rawDigits += allowedDigits;
        this.updateValueAndDisplay();
    }

    private updateValueAndDisplay() {
        this.updateDisplay();
        const finalValue = this.buildFormValue();
        this.onChange(finalValue);
        this.rawValueChange.emit(this.rawDigits);
        this.onTouched();
    }

    private updateDisplay() {
        let displayValue = '';
        if (this.maskType === "'card'") {
            displayValue = this.formatCardNumber();
        } else if (this.maskType === "'exp'") {
            displayValue = this.formatExpirationDate();
        } else {
            // For zipcode and CVV
            displayValue = 'x'.repeat(this.rawDigits.length);
        }

        const nativeEl = this.el.nativeElement;
        nativeEl.value = displayValue;

        // 👇 Dispatch 'input' event to ensure proper update/render
         this.isInternalUpdate = true;
        const event = new Event('input', { bubbles: true });
        nativeEl.dispatchEvent(event);
        this.isInternalUpdate = false;
    }

    private buildFormValue(): string {
        if (this.maskType === "'exp'") {
            // For expiration date, format as MM/YYYY for validation
            if (this.rawDigits.length >= 3) {
                const month = this.rawDigits.substring(0, 2);
                const year = this.rawDigits.substring(2);
                return `${month}/${year}`;
            } else if (this.rawDigits.length === 2) {
                return this.rawDigits;
            }
        }
        return this.rawDigits;
    }

    private formatCardNumber(): string {
        // Mask all digits with X, maintain the format with dashes
        const groups = [];
        for (let i = 0; i < this.rawDigits.length; i += 4) {
            const segmentLength = Math.min(4, this.rawDigits.length - i);
            groups.push('X'.repeat(segmentLength));
        }
        return groups.join('-');
    }

    private formatExpirationDate(): string {
        if (this.rawDigits.length <= 2) {
            // Mask the month
            return 'X'.repeat(this.rawDigits.length);
        } else {
            // Mask both month and year with proper format
            return 'XX/XXXX'.substring(0, this.rawDigits.length + 1);
        }
    }
}